import type {
  Chain,
  DynamicClient,
  VerifyResponse,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  updateAuthFromVerifyResponse,
  verifyMessageSignatureOwnership,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import type { AuthTypes } from '@walletconnect/types';
import { formatMessage } from '@walletconnect/utils';

const getAddressFromIss = (iss: string) => iss.split(':')[4];

type VerifyWalletConnectAuthsParams = {
  auths: AuthTypes.Cacao[];
  chain: Chain;
  walletDisplayName: string;
};

export const verifyWalletConnectAuths = async (
  { auths, chain, walletDisplayName }: VerifyWalletConnectAuthsParams,
  client: DynamicClient
) => {
  /**
   * The only differences between each auth object will be the signature (auth.s.s)
   * and the iss (auth.p.iss).
   *
   * The iss is comprised of 2 WC fields that are irrelevant to us and then the CAIP-10 token
   * of the address, where the first 2 fields are also split by the ":" character.
   *
   * Usually, there will be multiple auths if the wallet app bundles signatures for multiple
   * networks at once, so the only field that will change among auth iss will be the networkId under iss.
   *
   * Docs on Cacao: https://chainagnostic.org/CAIPs/caip-74
   */
  const visitedAddresses = new Set<string>();

  const authsFilteredByAddress = auths.filter((auth) => {
    const address = getAddressFromIss(auth.p.iss);

    if (!address || visitedAddresses.has(address)) {
      return false;
    }

    visitedAddresses.add(address);

    return true;
  });

  let lastResponse: VerifyResponse | undefined = undefined;

  /**
   * WalletConnect's auth object is what they call a "Cacao" object, with data on the SIWE message
   * and the signature itself.
   * By consulting how they implemented their "validateSignedCacao" function,
   * we are able to reconstruct the original message which can be used to verify the signature
   * in our backend.
   */
  for (const auth of authsFilteredByAddress) {
    const publicWalletAddress = getAddressFromIss(auth.p.iss);

    assertDefined(
      publicWalletAddress,
      `Unable to get public wallet address from WalletConnect Cacao object iss: ${auth.p.iss}`
    );

    const messageToSign = formatMessage(auth.p, auth.p.iss);

    const signature = auth.s.s;

    lastResponse = await verifyMessageSignatureOwnership(
      {
        chain,
        messageToSign,
        signature,
        walletAddress: publicWalletAddress,
        walletDisplayName,
        walletProviderType: WalletProviderEnum.WalletConnect,
      },
      client
    );
  }

  if (!lastResponse) {
    return;
  }

  /**
   * Adds the user with the new wallet verified credentials
   * This will add the new verified wallet accounts to the client`s state
   */
  updateAuthFromVerifyResponse({ response: lastResponse }, client);
};
